export type FinalizationState = 'processing' | 'ready' | 'failed' | 'technical_interrupted';

export type FinalizationSnapshot = {
  state: FinalizationState;
  ready: boolean;
  failed: boolean;
  interrupted: boolean;
  retryable: boolean;
  inputClosed: boolean;
  errorCode: string | null;
  processingStatus: any;
  interview: any;
  result: any;
};

export type FinalizationPollOutcome = {
  kind: 'ready' | 'failed' | 'interrupted' | 'exhausted' | 'network_exhausted' | 'cancelled';
  snapshot: FinalizationSnapshot | null;
  error?: any;
};

export type FinalizationPollOptions = {
  maxPolls: number;
  maxNetworkErrors: number;
  delayMs: number;
  wait?: (delayMs: number) => Promise<void>;
  onSnapshot?: (snapshot: FinalizationSnapshot) => void;
  isCancelled?: () => boolean;
};

export type FinishRecoveryOptions = {
  maxFinishAttempts: number;
  maxReconcilePolls: number;
  reconcileDelayMs: number;
  wait?: (delayMs: number) => Promise<void>;
};

const defaultWait = (delayMs: number) => new Promise<void>((resolve) => {
  window.setTimeout(resolve, delayMs);
});

const responseFrom = (value: any) => value?.response || value;

export const extractFinalizationSnapshot = (value: any): FinalizationSnapshot | null => {
  const response = responseFrom(value);
  const payload = response?.data?.data ?? null;
  const processingStatus = payload?.processing_status ?? null;
  const rawState = String(
    processingStatus?.state ||
    processingStatus?.stages?.final_result?.state ||
    (processingStatus?.ready === true ? 'ready' : processingStatus?.ready === false ? 'processing' : '')
  ).trim().toLowerCase();
  const state: FinalizationState | null = rawState === 'ready'
    || rawState === 'failed'
    || rawState === 'processing'
    || rawState === 'technical_interrupted'
    ? rawState
    : payload?.result
      ? 'ready'
      : null;

  if (!state) return null;

  return {
    state,
    ready: state === 'ready',
    failed: state === 'failed',
    interrupted: state === 'technical_interrupted',
    retryable: state === 'failed' && Boolean(processingStatus?.retryable ?? response?.data?.retryable),
    inputClosed: Boolean(processingStatus?.input_closed),
    errorCode: processingStatus?.error_code || response?.data?.error_code || null,
    processingStatus,
    interview: payload?.interview ?? null,
    result: payload?.result ?? null,
  };
};

export const isTemporaryFinalizationError = (error: any) => {
  const status = Number(error?.response?.status) || null;
  const code = String(error?.code || '').trim().toUpperCase();
  return code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    status === null ||
    status === 408 ||
    status === 425 ||
    status === 429 ||
    status >= 500;
};

export const pollFinalizationUntilTerminal = async (
  operation: () => Promise<any>,
  options: FinalizationPollOptions
): Promise<FinalizationPollOutcome> => {
  const wait = options.wait || defaultWait;
  const maxPolls = Math.max(1, Math.floor(options.maxPolls));
  const maxNetworkErrors = Math.max(0, Math.floor(options.maxNetworkErrors));
  let networkErrors = 0;
  let lastSnapshot: FinalizationSnapshot | null = null;

  for (let poll = 1; poll <= maxPolls; poll += 1) {
    if (options.isCancelled?.()) return { kind: 'cancelled', snapshot: lastSnapshot };
    try {
      const response = await operation();
      networkErrors = 0;
      const snapshot = extractFinalizationSnapshot(response);
      if (!snapshot) throw new Error('FINALIZATION_RESPONSE_INVALID');
      lastSnapshot = snapshot;
      options.onSnapshot?.(snapshot);
      if (snapshot.ready) return { kind: 'ready', snapshot };
      if (snapshot.failed) return { kind: 'failed', snapshot };
      if (snapshot.interrupted) return { kind: 'interrupted', snapshot };
    } catch (error: any) {
      const snapshot = extractFinalizationSnapshot(error);
      if (snapshot) {
        lastSnapshot = snapshot;
        options.onSnapshot?.(snapshot);
        if (snapshot.ready) return { kind: 'ready', snapshot };
        if (snapshot.failed) return { kind: 'failed', snapshot };
        if (snapshot.interrupted) return { kind: 'interrupted', snapshot };
      } else if (!isTemporaryFinalizationError(error)) {
        throw error;
      } else {
        networkErrors += 1;
        if (networkErrors > maxNetworkErrors) {
          return { kind: 'network_exhausted', snapshot: lastSnapshot, error };
        }
      }
    }

    if (poll < maxPolls) await wait(options.delayMs);
  }

  return { kind: 'exhausted', snapshot: lastSnapshot };
};

export const finishInterviewWithRecovery = async (
  finishOperation: () => Promise<any>,
  processingOperation: () => Promise<any>,
  options: FinishRecoveryOptions
): Promise<FinalizationSnapshot> => {
  const wait = options.wait || defaultWait;
  const maxFinishAttempts = Math.max(1, Math.floor(options.maxFinishAttempts));
  let lastError: any = null;

  for (let finishAttempt = 1; finishAttempt <= maxFinishAttempts; finishAttempt += 1) {
    try {
      const response = await finishOperation();
      const snapshot = extractFinalizationSnapshot(response);
      if (snapshot?.inputClosed) return snapshot;
      if (snapshot && response?.data?.status === true) return { ...snapshot, inputClosed: true };
      lastError = new Error('FINISH_RESPONSE_NOT_CONFIRMED');
    } catch (error: any) {
      const snapshot = extractFinalizationSnapshot(error);
      if (snapshot?.inputClosed) return snapshot;
      if (!isTemporaryFinalizationError(error)) throw error;
      lastError = error;
    }

    for (let poll = 1; poll <= Math.max(1, options.maxReconcilePolls); poll += 1) {
      try {
        const response = await processingOperation();
        const snapshot = extractFinalizationSnapshot(response);
        if (snapshot?.inputClosed) return snapshot;
        if (snapshot && !snapshot.inputClosed) break;
      } catch (error: any) {
        const snapshot = extractFinalizationSnapshot(error);
        if (snapshot?.inputClosed) return snapshot;
        if (!isTemporaryFinalizationError(error)) throw error;
        lastError = error;
      }
      if (poll < options.maxReconcilePolls) await wait(options.reconcileDelayMs);
    }

    if (finishAttempt < maxFinishAttempts) await wait(options.reconcileDelayMs);
  }

  throw lastError || new Error('FINISH_NOT_CONFIRMED');
};

export const createFinalizationCompletionCoordinator = () => {
  const active = new Map<string, Promise<FinalizationSnapshot>>();
  const completed = new Map<string, FinalizationSnapshot>();

  return {
    execute (
      key: string,
      operation: () => Promise<FinalizationSnapshot>,
      onConfirmed: (snapshot: FinalizationSnapshot) => Promise<void> | void
    ) {
      if (completed.has(key)) return Promise.resolve(completed.get(key) as FinalizationSnapshot);
      const existing = active.get(key);
      if (existing) return existing;

      const promise = (async () => {
        const snapshot = await operation();
        if (!completed.has(key)) {
          completed.set(key, snapshot);
          await onConfirmed(snapshot);
        }
        return snapshot;
      })().finally(() => {
        if (active.get(key) === promise) active.delete(key);
      });

      active.set(key, promise);
      return promise;
    },
  };
};
